import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import usePostcode from "@/Hooks/usePostCode.ts";
import { Button } from "flowbite-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchApi, getDataUser } from "@/Store/Action.ts";
import Swal from "sweetalert2";
import { CampaignData } from "@/Components/MyPage/ModalInvitation.tsx";
import { getWithExpiry } from "@/utils/token.ts";

type ModalPackageFormProps = {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  campaign: CampaignData
};

const ModalPackageForm = ({ showModal, setShowModal, campaign }: ModalPackageFormProps) => {
  const dataUser = useSelector((state) => state.ReducerDataUser?.dataUser?.data);
  const [isConfirm, setIsConfirm] = useState<boolean>(false);
  const [detailAddress, setDetailAddress] = useState("");
  const [receiverName, setReceiverName] = useState(dataUser?.name || "");
  const [receiverPhone, setReceiverPhone] = useState(dataUser?.phone || "");
  const [zipCode, setZipCode] = useState("");
  const [address, setAddress] = useState("");
  const kakaoContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getDataUser());
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
      document.documentElement.style.overflow = "auto";
    };
  }, [showModal]);

  const { openPostcode } = usePostcode({
    containerRef: kakaoContainerRef,
    onComplete: ({ address, zonecode }) => {
      setZipCode(zonecode);
      setAddress(address);
    },
  });

  const submitAddress = () => {
    if (!zipCode || !address || !receiverName || !receiverPhone) {
      Swal.fire({
        title: "모든 필드를 입력해주세요",
        icon: "warning",
        confirmButtonText: "확인했어요",
        customClass: {
          confirmButton:
            "bg-yellow-500 text-white px-6 py-2 rounded-full text-sm hover:bg-yellow-600",
          popup: "rounded-lg shadow text-center",
        },
      });

      return;
    }

    setIsConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    const id = getWithExpiry("id");
    const payload = {
      is_package: true,
      subscription_id: campaign.id,
      user_id: id,
      user_shipping_info: {
        address: address,
        address_detail: detailAddress,
        receiver_name: receiverName,
        receiver_phone: receiverPhone,
        zip_code: zipCode,
      },
    };

    try {
      const res = await fetchApi(`/user/attended`, {
        method: "PATCH",
        needAuth: true,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Failed to patch attended status.");
      }

      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error("Error patching attended:", err);
    }
  };

  if (!showModal) return null;

  return (
    <div className="text-center fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white max-h-[90vh] overflow-y-auto pt-9 pb-5 rounded-lg shadow-lg w-[90%] max-w-md relative">
        <X
          className="absolute right-4 top-4 w-6 h-6 text-gray-800 cursor-pointer"
          onClick={() => setShowModal(false)}
        />
        <div className="px-6 pb-5">
          <h2 className="font-bold text-[20px]">{!isConfirm ? "[배송지 입력]" : "[ 주소를 확인해주세요 ]"}</h2>
        </div>
        <div className="px-6 pt-5 border-t-2 border-[#E3E3E3]">

        {
          !isConfirm ?
            <>

                {/* Zip Code */}
                <div className="flex flex-col items-center justify-center">
                  <div className="px-4 py-1 mt-3 flex gap-2 w-full max-w-xl">
                    <div className="w-1/2">
                      <h3 className="font-bold mb-2 text-sm text-left">우편번호</h3>
                      <input
                        readOnly
                        type="text"
                        value={zipCode}
                        className="w-full border border-gray-300 text-xs rounded-md py-3"
                        placeholder="우편번호를 검색하세요"
                      />
                    </div>
                    <div className="w-1/2">
                      <h3 className="font-bold mb-2 text-sm text-left text-white">
                        우편번호
                      </h3>
                      <Button
                        className="bg-[#0F1E6C] w-full rounded-md"
                        onClick={openPostcode}
                      >
                        우편번호 찾기
                      </Button>
                    </div>
                  </div>

                  {/* Embed Iframe Container */}
                  <div
                    ref={kakaoContainerRef}
                    id="postcode-container"
                    className="hidden relative w-[95%] max-w-xl h-[400px] mt-4 mx-auto border border-gray-300 rounded-lg"
                  />
                </div>

                {/* Address */}
                <div className="px-4 py-1">
                  <h3 className="font-bold mb-2 text-sm text-left">기본주소</h3>
                  <input
                    type="text"
                    value={address}
                    className="w-full border border-gray-300 text-xs rounded-md py-3"
                    placeholder="기본주소가 자동으로 입력됩니다"
                    readOnly
                  />
                </div>

                {/* Detail Address */}
                <div className="px-4 py-1">
                  <h3 className="font-bold mb-2 text-sm text-left">상세주소</h3>
                  <input
                    type="text"
                    value={detailAddress}
                    onChange={(e) => setDetailAddress(e.target.value)}
                    className="w-full border border-gray-300 text-xs rounded-md py-3"
                    placeholder="예: 아파트 101동 202호"
                  />
                </div>

                {/* Name */}
                <div className="px-4 py-1">
                  <h3 className="font-bold mb-2 text-sm text-left">수령인 이름</h3>
                  <input
                    type="text"
                    value={receiverName}
                    onChange={(e) => setReceiverName(e.target.value)}
                    className="w-full border border-gray-300 text-xs rounded-md py-3"
                    placeholder="예: 김지민"
                  />
                </div>

                {/* Phone */}
                <div className="px-4 py-1">
                  <h3 className="font-bold mb-2 text-sm text-left">연락처</h3>
                  <input
                    type="text"
                    value={receiverPhone}
                    onChange={(e) => setReceiverPhone(e.target.value)}
                    className="w-full border border-gray-300 text-xs rounded-md py-3"
                    placeholder="예: 010-1234-5678"
                  />
                </div>

                {/* Submit Button */}
                <div className="px-4 py-1 mt-7">
                  <button className="w-full border rounded-full px-3 py-3 text-[15px] shadow-sm transition-colors bg-[#0F1E6C] text-white"
                          onClick={submitAddress}
                  >
                    등록
                  </button>
                </div>
            </>

            :
            <>
            <div className="px-6">
              <div className="text-gray-700 space-y-2 text-xs">
                <div className="flex ">
                  <div className="w-1/4 text-gray-400 text-left">우편번호</div>
                  <div className="w-3/4 text-black text-left">{zipCode}</div>
                </div>
                <div className="flex ">
                  <div className="w-1/4 text-gray-400 text-left">기본주소</div>
                  <div className="w-3/4 text-black text-left">
                    {address}{" "}
                    {detailAddress || ""}
                  </div>
                </div>
                <div className="flex ">
                  <div className="w-1/4 text-gray-400 text-left">수령인 이름</div>
                  <div className="w-3/4 text-black text-left">{receiverName}</div>
                </div>
                <div className="flex ">
                  <div className="w-1/4 text-gray-400 text-left">연락처</div>
                  <div className="w-3/4 text-black text-left">{receiverPhone}</div>
                </div>
              </div>
            </div>
              <div className="flex gap-4 mt-8">
                <button className="w-full border rounded-full px-3 py-3 text-[15px] shadow-sm transition-colors bg-[#0F1E6C] opacity-30 text-white"
                        onClick={()=>setIsConfirm(false)}
                >
                  취소
                </button>
                <button className="w-full border rounded-full px-3 py-3 text-[15px] shadow-sm transition-colors bg-[#0F1E6C] text-white"
                        onClick={handleConfirmSubmit}
                >
                  등록
                </button>
              </div>
            </>

        }

        </div>

      </div>
    </div>
  );
};

export default ModalPackageForm;
